import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Button, Box, Alert, CircularProgress } from '@mui/material';
import { Microsoft as MicrosoftIcon } from '@mui/icons-material';
import { loginRequest, ALLOWED_DOMAIN } from '../../config/azureConfig';

interface MicrosoftSignInButtonProps {
  onSuccess?: (user: any) => void;
  onError?: (error: string) => void;
}

export const MicrosoftSignInButton: React.FC<MicrosoftSignInButtonProps> = ({
  onSuccess,
  onError
}) => {
  const { instance } = useMsal();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateUser = (account: any): { isValid: boolean; error?: string } => {
    // Check email domain
    const email = account.username || account.idTokenClaims?.email;
    if (!email) {
      return { isValid: false, error: 'No se pudo obtener el email del usuario' };
    }

    const domain = email.split('@')[1];
    if (domain !== ALLOWED_DOMAIN) {
      return { 
        isValid: false, 
        error: `Solo se permiten usuarios del dominio @${ALLOWED_DOMAIN}` 
      };
    }

    // Note: Role validation is now handled by the backend
    // We only validate domain on the frontend for quick feedback

    return { isValid: true };
  };

  const handleLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await instance.loginPopup(loginRequest);
      
      if (response.account) {
        // Validate user before proceeding
        const validation = validateUser(response.account);
        
        if (!validation.isValid) {
          setError(validation.error || 'Usuario no válido');
          // Sign out the user since they don't have access
          await instance.logoutPopup();
          return;
        }

        // Send ID token to backend for validation and user creation
        const idToken = response.idToken;
        
        const backendResponse = await fetch('/api/auth/microsoft', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            idToken: idToken
          })
        });

        const result = await backendResponse.json();

        if (backendResponse.ok) {
          // Success - redirect to main page
          if (onSuccess) {
            onSuccess(result.user);
          } else {
            window.location.href = '/';
          }
        } else {
          setError(result.error || 'Error en la autenticación con el servidor');
        }
      }
    } catch (error: any) {
      console.error('Azure AD login error:', error);
      
      if (error.errorCode === 'user_cancelled') {
        setError('Inicio de sesión cancelado por el usuario');
      } else if (error.errorCode === 'access_denied') {
        setError('Acceso denegado. Contacte al administrador del sistema.');
      } else {
        setError('Error al iniciar sesión con Microsoft. Intente nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Button
        variant="outlined"
        fullWidth
        size="large"
        onClick={handleLogin}
        disabled={loading}
        startIcon={
          loading ? (
            <CircularProgress size={20} />
          ) : (
            <MicrosoftIcon />
          )
        }
        sx={{
          borderColor: '#0078d4',
          color: '#0078d4',
          '&:hover': {
            borderColor: '#106ebe',
            backgroundColor: 'rgba(0, 120, 212, 0.04)',
          },
          '&:disabled': {
            borderColor: '#ccc',
            color: '#ccc',
          },
          py: 1.5,
          textTransform: 'none',
          fontSize: '16px',
          fontWeight: 500,
        }}
      >
{loading ? 'Conectando...' : 'Conectar a Microsoft - Correo @arquitecturayconcreto.com'}
      </Button>
    </Box>
  );
};